/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { TextInput, View } from 'react-native';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { plural } from '../../shared/format/plural';
import { ticketColors } from '../../shared/theme/ticketColors';
import { OperationalScrollScreen } from '../../shared/ticket/OperationalScrollScreen';
import { PaperSheet } from '../../shared/ticket/PaperSheet';
import { TicketButton } from '../../shared/ticket/TicketButton';
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader';
import { ticketControls } from '../../shared/ticket/ticketControls.styles';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState'
import { showSuccess } from '../../shared/ui/appToast';
import { useScreenLoadShell } from '../../shared/ui/hooks-screen/useScreenLoad';
import { useResetModuleUiOnBlur } from '../../shell/navigation/useResetModuleUiOnBlur';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { PriceListFormModal } from '../components/PriceListFormModal';
import { PriceListRowCard } from '../components/PriceListRowCard';
import { usePriceListsListScreen } from '../application/usePriceListsListScreen';

export function PriceListsListScreen() {
  const vm = usePriceListsListScreen();
  const { refreshing, onRefresh } = useScreenLoadShell('PriceListsList', vm.loadFn, {
    minFocusIntervalMs: 60_000,
  });

  // Como Clientes: cerrar overlays al salir del tab.
  useResetModuleUiOnBlur(
    React.useCallback(() => vm.setEditorOpen(false), [vm.setEditorOpen]),
  );

  if (!vm.canView) {
    return (
      <ShellContentFrame>
        <ModuleEmptyState title="Sin acceso" subtitle="Pedile al dueño permiso para ver listas de precio." />
      </ShellContentFrame>
    );
  }

  // Sin listas y sin búsqueda activa: ocultar el buscador (#1459).
  const showSearch = vm.totalCount > 0 || vm.search.trim() !== '';

  const header = (
    <TicketModuleHeader
      kicker="Precios"
      title="Listas de precio"
      meta={`${vm.totalCount} ${plural(vm.totalCount, 'lista', 'listas')}`}
      // Sin listas el CTA vive en el empty state — no duplicar «Nueva lista».
      trailing={
        vm.canEdit && vm.totalCount > 0 ? (
          <TicketButton
            label="Nueva lista"
            variant="gold"
            onPress={vm.onCreate}
            testID="price-lists.new"
          />
        ) : null
      }
      tools={
        showSearch ? (
          <TextInput
            style={ticketControls.input}
            value={vm.search}
            onChangeText={vm.setSearch}
            placeholder="Buscar lista…"
            placeholderTextColor={ticketColors.ink3}
            accessibilityLabel="Buscar lista"
          />
        ) : null
      }
    />
  );

  const body = vm.loadError ? (
    <ModuleEmptyState
      variant="nest"
      kicker="Precios"
      title="No pudimos cargar las listas"
      subtitle={vm.loadError}
      primaryAction={{ label: 'Reintentar', onPress: () => void vm.loadFn() }}
    />
  ) : vm.lists.length === 0 ? (
    <ModuleEmptyState
      variant="nest"
      kicker="Precios"
      title={vm.totalCount === 0 ? 'Sin listas' : 'Sin resultados'}
      subtitle={
        vm.totalCount === 0
          ? 'Creá una lista para mayorista o clientes con precio especial.'
          : 'Probá con otro nombre o código.'
      }
      primaryAction={
        vm.totalCount === 0 && vm.canEdit
          ? { label: 'Nueva lista', onPress: vm.onCreate, testID: 'price-lists.empty.new' }
          : undefined
      }
      secondaryAction={
        vm.totalCount > 0 && vm.search.trim() !== ''
          ? { label: 'Limpiar búsqueda', onPress: () => vm.setSearch('') }
          : undefined
      }
    />
  ) : (
    <PaperSheet testID="price-lists.list.sheet">
      {vm.lists.map((list, idx) => (
        <PriceListRowCard
          key={list.id}
          list={list}
          group={idx === vm.lists.length - 1 ? 'groupedLast' : 'grouped'}
          itemCount={vm.itemCounts.get(list.id)}
          // Sin permiso de edición la fila abre el detalle en solo lectura.
          onPress={() => void vm.onEdit(list)}
        />
      ))}
    </PaperSheet>
  );

  const modals = (
    <PriceListFormModal
      visible={vm.editorOpen}
      initial={vm.editing}
      readOnly={!vm.canEdit}
      currentOnlineListName={
        vm.lists.find((l) => l.appliesOnline && l.id !== vm.editing?.id)?.name ?? null
      }
      onClose={() => vm.setEditorOpen(false)}
      onSave={async (input) => {
        try {
          await vm.saveList(input, vm.editing?.id ?? null);
          vm.setEditorOpen(false);
          showSuccess('Lista guardada.');
        } catch (e) {
          presentUnexpectedFailure({ module: 'priceLists', op: 'e', error: e, userMessage: 'No pudimos guardar.' });
        }
      }}
    />
  );

  return (
    <OperationalScrollScreen
      header={header}
      body={body}
      modals={modals}
      refreshing={refreshing}
      onRefresh={onRefresh}
    />
  );
}
